import React, { useEffect, useRef, useState } from "react";
import {
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ReplayIcon from "@mui/icons-material/Replay";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { getRequestWithToken } from "../../utils/ApiRequests";
import ObjectIdBadge from "../JobSectOccuRole/components/ObjectIdBadge";

export const CANDIDATE_ROLE_SEARCH_DELAY = 350;
export const canSearchCandidateRoles = (value = "") => value.trim().length >= 2;

const resultStyles = {
  family: { color: "#7C3AED", background: "rgba(124,58,237,0.12)", Icon: GroupsOutlinedIcon },
  group: { color: "#D66A00", background: "rgba(232,111,0,0.13)", Icon: AccountTreeOutlinedIcon },
  role: { color: "#147D70", background: "rgba(20,125,112,0.13)", Icon: BadgeOutlinedIcon },
};

const ResultLevel = ({ level, name = "" }) => {
  const { color, background, Icon } = resultStyles[level];
  return (
    <Box display="flex" alignItems="center" gap={0.75} minWidth={0} sx={{ flex: { xs: "1 1 100%", sm: "0 1 auto" } }}>
      <Box display="flex" alignItems="center" justifyContent="center" sx={{ width: 27, height: 27, borderRadius: 1.5, color, bgcolor: background, flexShrink: 0 }}>
        <Icon sx={{ fontSize: 16 }} />
      </Box>
      <Typography variant="body2" fontWeight={level === "role" ? 750 : 600} noWrap>{name}</Typography>
    </Box>
  );
};

const CandidateRoleQuickSearch = ({ onSelect = () => {}, onCopy = () => {} }) => {
  const [inputValue, setInputValue] = useState("");
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [retryToken, setRetryToken] = useState(0);
  const requestSequence = useRef(0);

  useEffect(() => {
    const query = inputValue.trim();
    const requestId = requestSequence.current + 1;
    requestSequence.current = requestId;
    if (!canSearchCandidateRoles(query)) {
      setOptions([]);
      setLoading(false);
      setError("");
      return undefined;
    }

    const timer = setTimeout(async () => {
      setLoading(true);
      setError("");
      try {
        const response = await getRequestWithToken(`../v3/admin/roles/search/global?q=${encodeURIComponent(query)}&limit=25`);
        if (requestId === requestSequence.current) setOptions(response.data?.data || []);
      } catch (requestError) {
        if (requestId === requestSequence.current) {
          setOptions([]);
          setError(requestError?.response?.data?.msg || "Unable to search Candidate Roles. Retry or change the query.");
        }
      } finally {
        if (requestId === requestSequence.current) setLoading(false);
      }
    }, CANDIDATE_ROLE_SEARCH_DELAY);

    return () => clearTimeout(timer);
  }, [inputValue, retryToken]);

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 1.5, borderRadius: 2.5, bgcolor: (theme) => theme.palette.mode === "dark" ? "#1b1b1b" : "#f1ede8", borderColor: (theme) => theme.palette.mode === "dark" ? "#303030" : "#d4cdc4", boxShadow: "0 8px 22px rgba(50,38,26,0.08)" }}>
      <Box mb={1.25}>
        <Typography variant="subtitle1" fontWeight={750}>Quick Candidate Role search</Typography>
        <Typography variant="caption" color="text.secondary">Search the canonical taxonomy and reveal a Candidate Role in the existing cards.</Typography>
      </Box>
      <Autocomplete
        options={options}
        value={null}
        inputValue={inputValue}
        loading={loading}
        filterOptions={(items) => items}
        getOptionLabel={(option) => option?.role_ui_label || option?.role_name || option?.role_id || ""}
        isOptionEqualToValue={(option, value) => option?._id === value?._id}
        onInputChange={(_, value, reason) => { if (reason !== "reset") setInputValue(value); }}
        onChange={(_, value) => {
          if (!value) return;
          setInputValue(value.role_ui_label || value.role_name || value.role_id || "");
          onSelect(value);
        }}
        noOptionsText={inputValue.trim().length < 2 ? "Type at least 2 characters" : error || "No matching Candidate Roles found"}
        PaperComponent={(paperProps) => <Paper {...paperProps} elevation={8} sx={{ mt: 0.75, borderRadius: 2, overflow: "hidden", bgcolor: (theme) => theme.palette.mode === "dark" ? "#222" : "#f4f1ed", border: "1px solid", borderColor: (theme) => theme.palette.mode === "dark" ? "#383838" : "#d8d0c7" }} />}
        ListboxProps={{ style: { maxHeight: 410, padding: 0 } }}
        renderInput={(params) => (
          <TextField
            {...params}
            size="small"
            placeholder="Search by Candidate Role name, role ID, alias, or ObjectId..."
            error={Boolean(error)}
            helperText={error || "Select a result to reveal its Family, Group, Role, and linked NCO panel."}
            sx={{ "& .MuiOutlinedInput-root": { bgcolor: (theme) => theme.palette.mode === "dark" ? "#242424" : "#faf8f5", borderRadius: 2, "&.Mui-focused": { boxShadow: "0 0 0 3px rgba(255,140,0,0.12)" }, "&.Mui-focused fieldset": { borderColor: "#FF8C00", borderWidth: 2 } } }}
            InputProps={{
              ...params.InputProps,
              startAdornment: <><InputAdornment position="start"><SearchIcon color="action" /></InputAdornment>{params.InputProps.startAdornment}</>,
              endAdornment: <>{loading && <CircularProgress color="inherit" size={18} />}{error && !loading && <IconButton size="small" aria-label="Retry Candidate Role search" onClick={() => setRetryToken((value) => value + 1)}><ReplayIcon fontSize="small" /></IconButton>}{params.InputProps.endAdornment}</>,
            }}
          />
        )}
        renderOption={(props, option) => {
          const group = option.group_ref;
          const family = group?.family_ref;
          const aliases = option.aliases?.en || [];
          return (
            <Box component="li" {...props} key={option._id} sx={{ alignItems: "flex-start !important", px: "16px !important", py: "12px !important", borderBottom: "1px solid", borderColor: (theme) => theme.palette.mode === "dark" ? "#333" : "#ded8d0", "&:hover, &[aria-selected='true']": { bgcolor: (theme) => theme.palette.mode === "dark" ? "rgba(255,140,0,0.12) !important" : "#e9e2d9 !important" } }}>
              <Box minWidth={0} width="100%">
                <Box display="flex" alignItems="center" gap={0.6} minWidth={0} flexWrap={{ xs: "wrap", sm: "nowrap" }}>
                  <ResultLevel level="family" name={family?.family_name || "Unassigned family"} />
                  <ChevronRightIcon sx={{ fontSize: 16, color: "text.disabled", display: { xs: "none", sm: "block" } }} />
                  <ResultLevel level="group" name={group?.group_name || "Unassigned group"} />
                  <ChevronRightIcon sx={{ fontSize: 16, color: "text.disabled", display: { xs: "none", sm: "block" } }} />
                  <ResultLevel level="role" name={option.role_ui_label || option.role_name || option.role_id} />
                </Box>
                <Box display="flex" gap={1.5} flexWrap="wrap" mt={0.8}>
                  <ObjectIdBadge objectId={family?._id} onCopy={onCopy} compact label="Family ID" />
                  <ObjectIdBadge objectId={group?._id} onCopy={onCopy} compact label="Group ID" />
                  <ObjectIdBadge objectId={option._id} onCopy={onCopy} compact label="Role ID" />
                </Box>
                <Box display="flex" gap={0.75} mt={0.7} flexWrap="wrap">
                  <Chip size="small" label={`Business ID: ${option.role_id}`} sx={{ height: 21, fontSize: 9.5, bgcolor: "rgba(20,125,112,0.12)", color: "#147D70" }} />
                  {aliases.slice(0, 3).map((alias) => <Chip key={alias} size="small" label={alias} variant="outlined" sx={{ height: 21, fontSize: 9.5 }} />)}
                </Box>
              </Box>
            </Box>
          );
        }}
      />
    </Paper>
  );
};

export default CandidateRoleQuickSearch;
